import React from 'react'
import { cn } from '@/utilities/ui'
import type { TableCell, CommitmentTerm } from '../types'
import { getCellContent, isSafeUrl } from '../utils'
import { StatusPill } from './StatusPill'

interface BodyCellProps {
  cell: TableCell
  cellIndex: number
  isLastColumn: boolean
  isLastRow: boolean
  isLeftOfHighlighted: boolean
  isHighlighted: boolean
  isComparison: boolean
  isFirstRow: boolean
  hasHeaderRow: boolean
  displayPeriod: 'hourly' | 'monthly'
  displayCommitmentTerm: CommitmentTerm
  isPriceFlipping: boolean
  isCommitmentFlipping: boolean
}

export const BodyCell: React.FC<BodyCellProps> = ({
  cell,
  cellIndex,
  isLastColumn,
  isLastRow,
  isLeftOfHighlighted,
  isHighlighted,
  isComparison,
  isFirstRow,
  hasHeaderRow,
  displayPeriod,
  displayCommitmentTerm,
  isPriceFlipping,
  isCommitmentFlipping,
}) => {
  const { variant } = cell
  const { primary, secondary } = getCellContent(cell, displayPeriod, displayCommitmentTerm)

  const cellClasses = cn(
    'text-center align-top',
    // Minimum width for mobile
    'min-w-40',
    // Desktop padding
    'px-7 py-7',
    // Tablet/Mobile padding
    'max-xl:px-5 max-xl:py-5',
    // Highlighted column styles for comparison tables
    isHighlighted && [
      'bg-white border-x border-civo-cloud shadow-[0px_2px_8px_rgba(0,0,0,0.08)]',
      'transition-colors duration-200 group-hover:bg-black/[0.02]',
      // Top styling when no header row exists and this is the first body row
      !hasHeaderRow && isFirstRow && 'border-t rounded-t-[20px]',
      // Bottom border on all rows except last
      !isLastRow && 'border-b border-b-civo-cloud',
      // Last row gets bottom border and rounded corners
      isLastRow && 'border-b border-b-civo-cloud rounded-b-[20px]',
    ],
    // Non-highlighted column styles
    !isHighlighted && [
      // Bottom border - remove on last row for comparison tables
      !(isComparison && isLastRow) && 'border-b border-civo-cloud/50',
      // Right border on all columns except the last, and except the column left of highlighted
      !isLastColumn && !isLeftOfHighlighted && 'border-r border-r-civo-cloud/50',
    ],
  )

  const renderContent = () => {
    if (variant === 'blank') {
      return <span className="sr-only">Empty cell</span>
    }

    if (variant === 'status') {
      return <StatusPill text={cell.statusText || ''} icon={cell.statusIcon} />
    }

    // Default, price, and commitment cells
    const isPriceCell = variant === 'price'
    const isCommitmentCell = variant === 'commitment'
    const hasFlipAnimation = isPriceCell || isCommitmentCell
    const shouldAnimate =
      (isPriceCell && isPriceFlipping) || (isCommitmentCell && isCommitmentFlipping)

    const content = (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-1 break-words',
          // Train board flip animation container
          hasFlipAnimation && '[perspective:1000px]',
        )}
      >
        {/* Mobile: body-small (14px), Desktop: body-copy-medium (16px) */}
        <span
          className={cn(
            'body-small xl:body-copy-medium text-black break-words',
            // Train board flip animation with reduced motion support
            hasFlipAnimation &&
              'transition-transform duration-150 ease-in-out [transform-style:preserve-3d] [backface-visibility:hidden] motion-reduce:transition-none',
            shouldAnimate && '[transform:rotateX(90deg)] motion-reduce:[transform:none]',
          )}
        >
          {primary}
        </span>
        {secondary && (
          <span
            className={cn(
              'body-xsmall italic text-black/50 break-words',
              // Train board flip animation with reduced motion support
              hasFlipAnimation &&
                'transition-transform duration-150 ease-in-out [transform-style:preserve-3d] [backface-visibility:hidden] motion-reduce:transition-none',
              shouldAnimate && '[transform:rotateX(90deg)] motion-reduce:[transform:none]',
            )}
          >
            {secondary}
          </span>
        )}
      </div>
    )

    // Wrap in link if provided (only for default variant with safe URLs)
    if (variant === 'default' && cell.link && isSafeUrl(cell.link)) {
      return (
        <a
          href={cell.link}
          className="block hover:underline focus:outline-none focus:ring-2 focus:ring-civo-lime focus:ring-offset-2 rounded"
        >
          {content}
        </a>
      )
    }

    return content
  }

  return (
    <td key={cell.id || cellIndex} className={cellClasses}>
      {renderContent()}
    </td>
  )
}
